import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';

import AnsweringView from 'views/Answering';
import { useFetch } from 'hooks';
import AccountDirectDialService from 'api/AccountDirectDial';
import SettingsActions from 'store/reducers/Settings';
import { formatPhone, handlePromise } from 'utils';

const Answering = ( { toggleLoading, toggleErrorAlert, toggleInfoAlert } ) => {
  const [loading, setLoading] = useState( false );
  const [data, setData] = useState( {} );
  const [errorNoAudiosModalVisible, setErrorNoAudiosModalVisible] = useState( false );

  const [phoneNumbers, phoneNumbersLoading] = useFetch( {
    initialState: [],
    promise: () => AccountDirectDialService.getDdis( { limit: 0 } ),
    format: ( dataToFormat ) => ( dataToFormat
      ? _.chain( dataToFormat.data )
        .sortBy( ( item ) => Number( item.createdAt ) )
        .map( ( item ) => ( {
          ...item,
          directDial: {
            ...item.directDial,
            number: formatPhone( item.directDial.number ),
          },
        } ) ).value() : [] ),
    toggleErrorAlert,
  } );

  const fetchPhoneNumber = useCallback( async ( id ) => {
    setLoading( true );

    const [errors, response, responseData] = await handlePromise(
      AccountDirectDialService.getDdi( id ),
    );
    setLoading( false );
    if ( !response.ok ) return toggleErrorAlert( errors );

    const formattedData = { ...responseData };

    if ( responseData.messageType === 'simple_message' && _.get( responseData, 'configData.audios.message_audio.uri' ) ) {
      formattedData.singleFile = responseData.configData.audios.message_audio;
    } else if ( responseData.messageType === 'poll' ) {
      if ( _.get( responseData, 'configData.audios.intro_audio.uri' ) ) {
        formattedData.introductionFile = responseData.configData.audios.intro_audio;
      }
      if ( _.get( responseData, 'configData.audios.question_audio.uri' ) ) {
        formattedData.optionsFile = responseData.configData.audios.question_audio;
      }
      if ( _.get( responseData, 'configData.audios.closing_audio.uri' ) ) {
        formattedData.farewellFile = responseData.configData.audios.closing_audio;
      }
    }

    if ( _.get( responseData, 'configData.audios.voicemail_audio.uri' ) ) {
      formattedData.mailboxFile = responseData.configData.audios.voicemail_audio;
    }
    setData( formattedData );
  }, [toggleErrorAlert] );

  const submitForm = useCallback( async ( formData ) => {
    toggleLoading( true );

    const messageMediaAudios = [];
    if ( formData.singleFile ) {
      messageMediaAudios.push( {
        audioType: 'message_audio',
        id: formData.singleFile.id,
      } );
    }
    if ( formData.introductionFile ) {
      messageMediaAudios.push( {
        audioType: 'intro_audio',
        id: formData.introductionFile.id,
      } );
    }
    if ( formData.optionsFile ) {
      messageMediaAudios.push( {
        audioType: 'question_audio',
        id: formData.optionsFile.id,
      } );
    }
    if ( formData.farewellFile ) {
      messageMediaAudios.push( {
        audioType: 'closing_audio',
        id: formData.farewellFile.id,
      } );
    }

    const voicemailMediaAudios = [];
    if ( formData.mailboxFile ) {
      voicemailMediaAudios.push( {
        audioType: 'voicemail_audio',
        id: formData.mailboxFile.id,
      } );
    }

    const dataToSend = {
      message: formData.message ? 1 : 0,
      messageType: formData.messageType,
      messageMediaAudioSrc: formData.messageMediaAudioSrc,
      voicemail: formData.voicemail ? 1 : 0,
      voicemailMediaAudioSrc: formData.voicemailMediaAudioSrc,
      messageMediaAudios,
      voicemailMediaAudios,
    };

    const [errors, response] = await handlePromise(
      AccountDirectDialService.updateDdi( formData.id, dataToSend ),
    );
    toggleLoading( false );
    if ( !response.ok ) {
      if ( errors === 'ERROR_MESSAGE_MEDIA_AUDIO_NOT_FOUND' ) {
        setErrorNoAudiosModalVisible( true );
      }
    } else if ( toggleInfoAlert ) toggleInfoAlert( 'dataSaved' );
  }, [toggleLoading, toggleInfoAlert, setErrorNoAudiosModalVisible] );

  return (
    <AnsweringView
      data={data}
      phoneNumbers={phoneNumbers}
      isLoading={phoneNumbersLoading || loading}
      isPhoneNumbersLoading={phoneNumbersLoading}
      onFetchPhoneNumber={fetchPhoneNumber}
      onSubmit={submitForm}
      errorNoAudiosModalVisible={errorNoAudiosModalVisible}
      setErrorNoAudiosModalVisible={setErrorNoAudiosModalVisible}
    />
  );
};

const mapDispatchToProps = ( {
  toggleLoading: SettingsActions.toggleLoading,
  toggleErrorAlert: SettingsActions.toggleErrorAlert,
  toggleInfoAlert: SettingsActions.toggleInfoAlert,
} );
export default connect( null, mapDispatchToProps )( Answering );
